import { useEffect, useRef, useState } from 'react'
import { XIcon } from '../icons'
import './CameraScanner.css'

/**
 * Telefon kamerasıyla barkod okutma (20 Eki 2026). Garson ekranında, üst
 * çubuktaki barkod düğmesiyle açılıyor; kasadaki USB okuyucunun telefondaki
 * karşılığı.
 *
 * Pencere açık kaldıkça okutulan her barkod `onCode`'a gidiyor; garson birkaç
 * ürünü arka arkaya okutabiliyor. Bir barkod kameranın önünde durduğu sürece
 * bir kez sayılıyor; yeniden sayılması için en az REPEAT_MS görüntüden çıkması
 * gerekiyor (kutu kameranın önünde tutulunca üst üste eklenmesin; aynı
 * üründen ikincisi için çekip yeniden göstermek yetiyor).
 *
 * Okuyucu: tarayıcının kendi BarcodeDetector'ı varsa o (Android Chrome), yoksa
 * barcode-detector paketi (zxing-wasm; iPhone Safari). Paket yalnız pencere
 * açılınca yükleniyor, motoru (wasm, ~1 MB) kendi sunucumuzdan geliyor.
 */
const REPEAT_MS = 1500
const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'] as const

interface Detector {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>
}

async function makeDetector(): Promise<Detector> {
  const Native = (window as unknown as { BarcodeDetector?: { new (o: object): Detector; getSupportedFormats?: () => Promise<string[]> } })
    .BarcodeDetector
  if (Native?.getSupportedFormats) {
    try {
      const have = await Native.getSupportedFormats()
      const formats = FORMATS.filter((f) => have.includes(f))
      if (formats.includes('ean_13')) return new Native({ formats })
    } catch {
      // Yerleşik okuyucu sorunluysa pakete düşülüyor.
    }
  }
  const [{ BarcodeDetector, setZXingModuleOverrides }, wasm] = await Promise.all([
    import('barcode-detector/ponyfill'),
    import('zxing-wasm/reader/zxing_reader.wasm?url'),
  ])
  setZXingModuleOverrides({
    locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasm.default : prefix + path),
  })
  return new BarcodeDetector({ formats: [...FORMATS] }) as unknown as Detector
}

export function CameraScanner({
  onCode,
  onClose,
}: {
  /** Okutulan barkod; dönen yazı pencerenin altında gösteriliyor. */
  onCode: (code: string) => { text: string; ok: boolean }
  onClose: () => void
}) {
  const video = useRef<HTMLVideoElement>(null)
  const [state, setState] = useState<'starting' | 'live' | 'denied' | 'error'>('starting')
  const [last, setLast] = useState<{ text: string; ok: boolean; at: number } | null>(null)
  // Ref ile: sayfa her çizildiğinde yeni fonksiyon gelse de kamera kapanıp
  // açılmasın.
  const onCodeRef = useRef(onCode)
  onCodeRef.current = onCode
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    let stream: MediaStream | null = null
    let stopped = false
    let timer = 0
    const seen = new Map<string, number>()

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
          audio: false,
        })
        if (stopped) return
        const v = video.current!
        v.srcObject = stream
        await v.play()
        const detector = await makeDetector()
        if (stopped) return
        setState('live')

        const tick = async () => {
          if (stopped) return
          try {
            if (v.readyState >= 2) {
              const found = await detector.detect(v)
              const now = Date.now()
              for (const f of found) {
                const code = f.rawValue?.trim()
                if (!code) continue
                // Her görüşte güncelleniyor: önünde durdukça süre hiç dolmuyor.
                const prev = seen.get(code) ?? 0
                seen.set(code, now)
                if (now - prev < REPEAT_MS) continue
                const r = onCodeRef.current(code)
                setLast({ ...r, at: now })
                navigator.vibrate?.(r.ok ? 40 : [30, 60, 30])
              }
            }
          } catch {
            // Tek karede okuma hatası: sonraki karede yeniden denenir.
          }
          timer = window.setTimeout(tick, 220)
        }
        void tick()
      } catch (err) {
        if (stopped) return
        const name = (err as { name?: string })?.name
        setState(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'error')
      }
    }
    void start()

    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onCloseRef.current()
    window.addEventListener('keydown', onKey)
    return () => {
      stopped = true
      window.clearTimeout(timer)
      stream?.getTracks().forEach((t) => t.stop())
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  return (
    <div className="camscan" role="dialog" aria-modal="true" aria-label="Barkod okut">
      <div className="camscan__card">
        <header className="camscan__head">
          <h2>Barkod okut</h2>
          <button type="button" className="camscan__x" onClick={onClose} aria-label="Kapat">
            <XIcon />
          </button>
        </header>

        <div className="camscan__view">
          <video ref={video} playsInline muted />
          {state === 'live' && <span className="camscan__frame" aria-hidden="true" />}
          {state === 'starting' && <p className="camscan__msg">Kamera açılıyor…</p>}
          {state === 'denied' && (
            <p className="camscan__msg">
              Kamera izni verilmedi. Tarayıcının adres çubuğundaki kamera simgesinden izin verip yeniden
              deneyin.
            </p>
          )}
          {state === 'error' && <p className="camscan__msg">Kamera açılamadı.</p>}
        </div>

        <p className={`camscan__last ${last ? (last.ok ? 'is-ok' : 'is-warn') : ''}`} aria-live="polite">
          {last ? last.text : 'Barkodu çerçevenin içine tutun'}
        </p>
      </div>
    </div>
  )
}
